import React from 'react';
import { LoadingOverlay, Modal } from '@mantine/core';
import useChargingServiceImages from '../../../hooks/charging-services/useChargingServiceImages.ts';
import { Carousel } from '@mantine/carousel';
import useChargingService from '../../../hooks/charging-services/useChargingService.ts';
import { ServiceType } from '../../../models/ServiceType.ts';
import useParkingService from '../../../hooks/parking-services/useParkingService.ts';
import useFuelingService from '../../../hooks/fueling-services/useFuelingService.ts';
import useParkingServiceImages from '../../../hooks/parking-services/useParkingServiceImages.ts';
import useFuelingServiceImages from '../../../hooks/fueling-services/useFuelingServiceImages.ts';
import useMaintenanceService from '../../../hooks/maintenance-services/useMaintenanceService.ts';
import useInspectionService from '../../../hooks/inspection-services/useInspectionService.ts';
import useMaintenanceServiceImages from '../../../hooks/maintenance-services/useMaintenanceServiceImages.ts';
import useInspectionServiceImages from '../../../hooks/inspection-services/useInspectionServiceImages.ts';

interface ImageDisplayCarouselProps {
  serviceId: string;
  serviceType: ServiceType;
  isServiceImageCarouselOpen: boolean;
  setIsServiceImageCarouselOpen: (isServiceImageCarouselOpen: boolean) => void;
}

const usePlaceService = (serviceType: ServiceType, serviceId: string) => {
  const chargingService = useChargingService(serviceId, {
    enabled: serviceType === 'CHARGING',
  });
  const parkingService = useParkingService(serviceId, {
    enabled: serviceType === 'PARKING',
  });
  const fuelingService = useFuelingService(serviceId, {
    enabled: serviceType === 'FUELING',
  });
  const maintenanceService = useMaintenanceService(serviceId, {
    enabled: serviceType === 'MAINTENANCE',
  });
  const inspectionService = useInspectionService(serviceId, {
    enabled: serviceType === 'INSPECTION',
  });

  const map = {
    CHARGING: chargingService,
    PARKING: parkingService,
    FUELING: fuelingService,
    MAINTENANCE: maintenanceService,
    INSPECTION: inspectionService,
  };
  return map[serviceType];
};

const usePlaceServiceImages = (
  serviceType: ServiceType,
  serviceId: string,
  imageIds: string[],
) => {
  const chargingServiceImages = useChargingServiceImages(serviceId, imageIds, {
    enabled: serviceType === 'CHARGING',
  });
  const parkingServiceImages = useParkingServiceImages(serviceId, imageIds, {
    enabled: serviceType === 'PARKING',
  });
  const fuelingServiceImages = useFuelingServiceImages(serviceId, imageIds, {
    enabled: serviceType === 'FUELING',
  });
  const maintenanceServiceImages = useMaintenanceServiceImages(
    serviceId,
    imageIds,
    { enabled: serviceType === 'MAINTENANCE' },
  );
  const inspectionServiceImages = useInspectionServiceImages(
    serviceId,
    imageIds,
    { enabled: serviceType === 'INSPECTION' },
  );

  const map = {
    CHARGING: chargingServiceImages,
    PARKING: parkingServiceImages,
    FUELING: fuelingServiceImages,
    MAINTENANCE: maintenanceServiceImages,
    INSPECTION: inspectionServiceImages,
  };
  return map[serviceType];
};

const ImageDisplayCarousel: React.FC<ImageDisplayCarouselProps> = ({
  serviceId,
  serviceType,
  isServiceImageCarouselOpen,
  setIsServiceImageCarouselOpen,
}) => {
  const { data: service, isLoading: isPlaceServiceLoading } = usePlaceService(
    serviceType,
    serviceId,
  );

  const serviceIdValue = service?.id;
  const imageIdsValue = service?.imageIds ?? [];

  const imageQueries = usePlaceServiceImages(
    serviceType,
    serviceIdValue,
    imageIdsValue,
  );
  const imageResponses = imageQueries.map((q) => q.data);
  const isImagesLoading = imageQueries.some((q) => q.isLoading);

  if (isPlaceServiceLoading || isImagesLoading)
    return <LoadingOverlay visible={false} />;

  return (
    <>
      {imageResponses.length ? (
        <Modal
          opened={isServiceImageCarouselOpen}
          onClose={() => setIsServiceImageCarouselOpen(false)}
          keepMounted={false}
          padding={0}
          centered
          withCloseButton={false}
          size="80%"
          styles={{
            content: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
            body: {
              height: 600,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            },
            inner: {
              padding: 0,
            },
          }}
          overlayProps={{
            backgroundOpacity: 0.4,
            blur: 3,
          }}
        >
          <Carousel
            key={isServiceImageCarouselOpen ? 'open' : 'closed'}
            type="container"
            slideGap="lg"
            withControls
            withIndicators
            controlsOffset="xl"
            controlSize={40}
            emblaOptions={{
              loop: true,
              dragFree: false,
              align: 'center',
            }}
            styles={{
              root: {
                width: '90vw',
                maxWidth: 1200,
                height: 600,
                overflow: 'hidden',
              },
              slide: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                height: 600,
              },
              control: {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              },
              indicator: {
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
              },
            }}
          >
            {imageResponses.map((response, idx) => {
              return (
                <Carousel.Slide key={idx}>
                  <img
                    src={response.signedUrl}
                    alt={response.imageId}
                    style={{
                      maxWidth: 900,
                      maxHeight: 500,
                      width: 'auto',
                      height: 'auto',
                    }}
                  />
                </Carousel.Slide>
              );
            })}
          </Carousel>
        </Modal>
      ) : null}
    </>
  );
};

export default ImageDisplayCarousel;
